/**
 * Simple reactive signal implementation for Poker Reborn
 *
 * Provides reactive state management without external dependencies.
 */

export type Subscriber<T> = (value: T) => void;
export type Unsubscribe = () => void;

/**
 * A reactive signal that holds a value and notifies subscribers on change
 */
export class Signal<T> {
  private value: T;
  private subscribers: Set<Subscriber<T>> = new Set();

  constructor(initialValue: T) {
    this.value = initialValue;
  }

  /**
   * Get the current value
   */
  get(): T {
    return this.value;
  }

  /**
   * Set a new value and notify subscribers if changed
   */
  set(newValue: T): void {
    if (this.value !== newValue) {
      this.value = newValue;
      this.notify();
    }
  }

  /**
   * Update the value using a function
   */
  update(fn: (current: T) => T): void {
    this.set(fn(this.value));
  }

  /**
   * Subscribe to value changes
   */
  subscribe(subscriber: Subscriber<T>): Unsubscribe {
    this.subscribers.add(subscriber);

    // Call immediately with current value
    subscriber(this.value);

    return () => {
      this.subscribers.delete(subscriber);
    };
  }

  /**
   * Subscribe without immediate call
   */
  onChange(subscriber: Subscriber<T>): Unsubscribe {
    this.subscribers.add(subscriber);
    return () => {
      this.subscribers.delete(subscriber);
    };
  }

  /**
   * Notify all subscribers
   */
  private notify(): void {
    this.subscribers.forEach((subscriber) => {
      try {
        subscriber(this.value);
      } catch (error) {
        console.error('Error in signal subscriber:', error);
      }
    });
  }
}

/**
 * Create a computed signal that derives its value from other signals
 */
export function computed<T>(
  compute: () => T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps: Signal<any>[]
): Signal<T> {
  const result = new Signal<T>(compute());

  // Update when any dependency changes
  deps.forEach((dep) => {
    dep.onChange(() => {
      result.set(compute());
    });
  });

  return result;
}

/**
 * Create a signal
 */
export function signal<T>(initialValue: T): Signal<T> {
  return new Signal(initialValue);
}
